/**
 * iter251 — Support policy editor: AI triage budget ceilings + first-response
 * SLA targets. One dialog, two independent saves, because PATCH
 * /support/settings only writes the fields it is given.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Save, Sparkles, Timer, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const SEVERITIES = [
  { key: 'critical', label: 'Critical', hint: 'outage, data loss, blocked payment' },
  { key: 'high', label: 'High', hint: 'core feature broken for one user' },
  { key: 'medium', label: 'Medium', hint: 'degraded but workaroundable' },
  { key: 'low', label: 'Low', hint: 'cosmetic, questions, praise' },
];

const TIMEZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver',
  'America/Los_Angeles', 'Europe/London', 'Europe/Berlin', 'Asia/Kolkata',
  'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney',
];

const humanise = (minutes) => {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 60) return `${n}m`;
  if (n % 1440 === 0) return `${n / 1440}d`;
  if (n % 60 === 0) return `${n / 60}h`;
  return `${(n / 60).toFixed(1)}h`;
};

export const SupportPolicyDialog = ({ open, onOpenChange, settings, onSaved }) => {
  const [caps, setCaps] = useState({ daily: '', hourly: '' });
  const [targets, setTargets] = useState({});
  const [clockMode, setClockMode] = useState('24_7');
  const [tz, setTz] = useState('UTC');
  const [startHour, setStartHour] = useState(9);
  const [endHour, setEndHour] = useState(18);
  const [savingCaps, setSavingCaps] = useState(false);
  const [savingSla, setSavingSla] = useState(false);

  useEffect(() => {
    if (!open || !settings) return;
    setCaps({
      daily: String(settings.triage_daily_cap ?? 200),
      hourly: String(settings.triage_hourly_cap ?? 40),
    });
    const t = {};
    SEVERITIES.forEach(({ key }) => {
      t[key] = String(settings.sla_targets?.[key] ?? '');
    });
    setTargets(t);
    setClockMode(settings.sla_clock_mode || '24_7');
    setTz(settings.sla_timezone || 'UTC');
    setStartHour(settings.sla_business_start_hour ?? 9);
    setEndHour(settings.sla_business_end_hour ?? 18);
  }, [open, settings]);

  const saveCaps = async () => {
    const daily = parseInt(caps.daily, 10);
    const hourly = parseInt(caps.hourly, 10);
    if (!Number.isFinite(daily) || daily < 1 || !Number.isFinite(hourly) || hourly < 1) {
      toast.error('Both caps must be at least 1', { id: 'caps-invalid' });
      return;
    }
    if (hourly > daily) {
      toast.error('The hourly cap cannot exceed the daily cap', { id: 'caps-order' });
      return;
    }
    setSavingCaps(true);
    try {
      const r = await api.patch('/support/settings', {
        triage_daily_cap: daily, triage_hourly_cap: hourly,
      });
      onSaved?.(r.data);
      toast.success(`Triage budget saved — ${daily}/day, ${hourly}/hour`, { id: 'caps-saved' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the budget', { id: 'caps-fail' });
    } finally {
      setSavingCaps(false);
    }
  };

  const saveSla = async () => {
    const payload = { sla_targets: {}, sla_clock_mode: clockMode };
    for (const { key, label } of SEVERITIES) {
      const v = parseInt(targets[key], 10);
      if (!Number.isFinite(v) || v < 5 || v > 20160) {
        toast.error(`${label} target must be 5 minutes to 14 days`, { id: 'sla-invalid' });
        return;
      }
      payload.sla_targets[key] = v;
    }
    if (clockMode === 'business_hours') {
      if (endHour <= startHour) {
        toast.error('The business day must end after it starts', { id: 'sla-hours' });
        return;
      }
      payload.sla_timezone = tz;
      payload.sla_business_start_hour = Number(startHour);
      payload.sla_business_end_hour = Number(endHour);
    }
    setSavingSla(true);
    try {
      const r = await api.patch('/support/settings', payload);
      onSaved?.(r.data);
      toast.success('SLA policy saved', { id: 'sla-saved' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the SLA policy', { id: 'sla-fail' });
    } finally {
      setSavingSla(false);
    }
  };

  const resetDefaults = () => {
    const d = settings?.sla_defaults || { critical: 60, high: 240, medium: 1440, low: 4320 };
    const t = {};
    SEVERITIES.forEach(({ key }) => { t[key] = String(d[key]); });
    setTargets(t);
  };

  const budget = settings?.triage_budget;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="support-policy-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-slate-100">Support policy</DialogTitle>
          <DialogDescription className="text-slate-400">
            Two ceilings on AI spend, and the clock you promise to answer against.
          </DialogDescription>
        </DialogHeader>

        {/* AI triage budget */}
        <section className="rounded-md border border-violet-900/50 bg-violet-950/10 p-4">
          <h3 className="text-sm font-semibold text-violet-200 flex items-center gap-2 mb-1">
            <Sparkles className="h-4 w-4" /> AI triage budget
          </h3>
          <p className="text-xs text-slate-400 mb-3 leading-relaxed">
            One model call per inbound ticket, so a spam wave is a spend wave. When
            either ceiling is reached, tickets are saved <strong>untriaged</strong> into
            the &ldquo;Needs a human&rdquo; queue and AI auto-replies pause until
            00:00&nbsp;UTC. Nothing is dropped.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Calls per day (UTC)</Label>
              <Input
                type="number"
                min={1}
                value={caps.daily}
                onChange={(e) => setCaps((c) => ({ ...c, daily: e.target.value }))}
                className="mt-1 bg-[#0A1024] border-[#1F2A4D] text-slate-100"
                data-testid="policy-daily-cap"
              />
              {budget && (
                <p className="text-[10px] text-slate-500 mt-1 tabular-nums">
                  {budget.day_used} used today
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs text-slate-400">Calls per hour (burst brake)</Label>
              <Input
                type="number"
                min={1}
                value={caps.hourly}
                onChange={(e) => setCaps((c) => ({ ...c, hourly: e.target.value }))}
                className="mt-1 bg-[#0A1024] border-[#1F2A4D] text-slate-100"
                data-testid="policy-hourly-cap"
              />
              {budget && (
                <p className="text-[10px] text-slate-500 mt-1 tabular-nums">
                  {budget.hour_used} used this hour
                </p>
              )}
            </div>
          </div>
          <div className="flex justify-end mt-3">
            <Button
              size="sm"
              onClick={saveCaps}
              disabled={savingCaps}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="policy-save-caps"
            >
              {savingCaps
                ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Saving…</>
                : <><Save className="h-3.5 w-3.5 mr-1.5" /> Save budget</>}
            </Button>
          </div>
        </section>

        {/* SLA policy */}
        <section className="rounded-md border border-[#1F2A4D] bg-[#0A1024] p-4">
          <h3 className="text-sm font-semibold text-slate-100 flex items-center gap-2 mb-1">
            <Timer className="h-4 w-4 text-amber-400" /> First-response SLA
          </h3>
          <p className="text-xs text-slate-400 mb-3 leading-relaxed">
            Measured from ticket creation to the first reply, human or automatic. A
            ticket turns amber at 75% of its target and red when it passes it.
            Untriaged tickets are measured against the <strong>High</strong> target
            until the AI assigns a severity.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {SEVERITIES.map(({ key, label, hint }) => (
              <div key={key}>
                <Label className="text-xs text-slate-300">{label}</Label>
                <Input
                  type="number"
                  min={5}
                  max={20160}
                  value={targets[key] ?? ''}
                  onChange={(e) => setTargets((t) => ({ ...t, [key]: e.target.value }))}
                  className="mt-1 bg-[#0F1730] border-[#1F2A4D] text-slate-100"
                  data-testid={`policy-sla-${key}`}
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  {humanise(targets[key]) || 'minutes'} · {hint}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Clock</Label>
              <Select value={clockMode} onValueChange={setClockMode}>
                <SelectTrigger
                  className="mt-1 bg-[#0F1730] border-[#1F2A4D] text-slate-100"
                  data-testid="policy-sla-clock-mode"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24_7">24/7 — wall clock, nights and weekends count</SelectItem>
                  <SelectItem value="business_hours">Business hours — Mon–Fri only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {clockMode === 'business_hours' && (
              <div>
                <Label className="text-xs text-slate-400">Timezone</Label>
                <Select value={tz} onValueChange={setTz}>
                  <SelectTrigger
                    className="mt-1 bg-[#0F1730] border-[#1F2A4D] text-slate-100"
                    data-testid="policy-sla-timezone"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((z) => (
                      <SelectItem key={z} value={z}>{z}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {clockMode === 'business_hours' && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">Day starts (hour)</Label>
                <Input
                  type="number"
                  min={0}
                  max={23}
                  value={startHour}
                  onChange={(e) => setStartHour(e.target.value)}
                  className="mt-1 bg-[#0F1730] border-[#1F2A4D] text-slate-100"
                  data-testid="policy-sla-start-hour"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Day ends (hour)</Label>
                <Input
                  type="number"
                  min={1}
                  max={24}
                  value={endHour}
                  onChange={(e) => setEndHour(e.target.value)}
                  className="mt-1 bg-[#0F1730] border-[#1F2A4D] text-slate-100"
                  data-testid="policy-sla-end-hour"
                />
              </div>
            </div>
          )}

          <div className="flex justify-between items-center mt-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={resetDefaults}
              className="text-slate-400 hover:text-slate-200"
              data-testid="policy-sla-reset"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Reset to 1h / 4h / 24h / 72h
            </Button>
            <Button
              size="sm"
              onClick={saveSla}
              disabled={savingSla}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="policy-save-sla"
            >
              {savingSla
                ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Saving…</>
                : <><Save className="h-3.5 w-3.5 mr-1.5" /> Save SLA</>}
            </Button>
          </div>
        </section>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-slate-300 hover:bg-[#0A1024]"
            data-testid="policy-close"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SupportPolicyDialog;
